import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/**
 * Breadcrumb override context.
 *
 * Detail / edit pages register a human-readable label for the ID segment that
 * appears in their URL (e.g. "NS-3", "RN-2025-0042", "Auto servis Petrović").
 * The Breadcrumb component looks up the current path's ID segments in this map
 * and displays the override instead of a truncated GUID.
 *
 * The URL itself is not modified — overrides are purely a display concern and
 * are automatically removed when the page that registered them unmounts.
 */
interface BreadcrumbOverrideContextValue {
  labels: Record<string, string>;
  setLabel: (segment: string, label: string) => void;
  clearLabel: (segment: string) => void;
}

const BreadcrumbOverrideContext = createContext<BreadcrumbOverrideContextValue | undefined>(
  undefined,
);

export const BreadcrumbOverrideProvider = ({ children }: { children: ReactNode }) => {
  const [labels, setLabels] = useState<Record<string, string>>({});

  const setLabel = useCallback((segment: string, label: string) => {
    setLabels((prev) => (prev[segment] === label ? prev : { ...prev, [segment]: label }));
  }, []);

  const clearLabel = useCallback((segment: string) => {
    setLabels((prev) => {
      if (!(segment in prev)) return prev;
      const next = { ...prev };
      delete next[segment];
      return next;
    });
  }, []);

  const value = useMemo(() => ({ labels, setLabel, clearLabel }), [labels, setLabel, clearLabel]);

  return (
    <BreadcrumbOverrideContext.Provider value={value}>
      {children}
    </BreadcrumbOverrideContext.Provider>
  );
};

/**
 * Returns the current map of segment → label overrides.
 * Used by the Breadcrumb component when rendering.
 */
export const useBreadcrumbOverrideMap = (): Record<string, string> => {
  const ctx = useContext(BreadcrumbOverrideContext);
  return ctx?.labels ?? {};
};

/**
 * Registers a human-readable breadcrumb label for a URL segment (typically a GUID).
 *
 * - Pass `undefined`/`null` for `label` while data is loading — nothing is registered.
 * - Once both `segment` and `label` are provided the override is applied.
 * - When the component unmounts (or either input becomes falsy) the override is removed.
 * - Safe to call outside the provider (no-op).
 */
export const useBreadcrumbOverride = (
  segment: string | undefined | null,
  label: string | undefined | null,
): void => {
  const ctx = useContext(BreadcrumbOverrideContext);
  // Extract the stable callbacks; depending on the full `ctx` object would
  // retrigger this effect every time `labels` changes (because the provider
  // memoizes a new value object), and the cleanup would clear the label,
  // causing an infinite setState → re-render loop.
  const setLabel = ctx?.setLabel;
  const clearLabel = ctx?.clearLabel;
  const registeredSegment = useRef<string | null>(null);

  useEffect(() => {
    if (!setLabel || !clearLabel) return;

    if (!segment || !label) {
      if (registeredSegment.current) {
        clearLabel(registeredSegment.current);
        registeredSegment.current = null;
      }
      return;
    }

    if (registeredSegment.current && registeredSegment.current !== segment) {
      clearLabel(registeredSegment.current);
    }

    setLabel(segment, label);
    registeredSegment.current = segment;

    return () => {
      if (registeredSegment.current) {
        clearLabel(registeredSegment.current);
        registeredSegment.current = null;
      }
    };
  }, [segment, label, setLabel, clearLabel]);
};
